import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, FileText, Image, Loader2, CheckCircle2, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import api, { createExpense } from '@/lib/api';
import { toast } from 'sonner';

const FileUploadCard = ({ onExpensesParsed, eventId, compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large! Maximum 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/expenses/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setParsedData(response.data);
      setShowPreview(true);
      toast.success(`Found ${response.data.expenses_found} expense(s)!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process file');
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleSubmitExpenses = async () => {
    if (!eventId) {
      toast.error('Please select an event first');
      return;
    }
    
    if (!parsedData || parsedData.expenses.length === 0) {
      toast.error('No expenses to submit');
      return;
    }

    setSubmitting(true);
    let successCount = 0;
    
    try {
      for (const expense of parsedData.expenses) {
        await createExpense({
          event_id: eventId,
          amount: parseFloat(expense.amount) || 0,
          currency: expense.currency || 'INR',
          category: expense.category || 'Miscellaneous',
          vendor: expense.vendor || '',
          description: expense.description || 'Uploaded expense',
        });
        successCount++;
      }
      
      toast.success(`${successCount} expense(s) logged successfully!`);
      setShowPreview(false);
      setParsedData(null);
      
      // Also call the callback if provided
      if (onExpensesParsed) {
        onExpensesParsed(parsedData.expenses);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log expenses');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseInForm = () => {
    if (parsedData && parsedData.expenses.length > 0 && onExpensesParsed) {
      onExpensesParsed(parsedData.expenses);
      setShowPreview(false);
      setParsedData(null);
      toast.info('Expense loaded into form. Click "Log Expense" to submit.');
    }
  };

  return (
    <>
      {compact ? (
        // Compact version - just the upload area
        <div>
          <input
            type="file"
            id="file-upload-compact"
            accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="file-upload-compact" className="cursor-pointer">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:bg-accent/20 transition-colors">
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                  <span className="text-sm text-muted-foreground">Processing...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <div className="text-left">
                    <p className="font-medium text-sm">Click to upload</p>
                    <p className="text-xs text-muted-foreground">Excel, PDF, or image</p>
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>
      ) : (
        // Full version with Card wrapper
        <Card className="border-2 border-dashed border-primary/30 hover:border-primary/60 transition-colors">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Upload className="h-5 w-5 text-primary" />
              <CardTitle>Upload File</CardTitle>
            </div>
            <CardDescription>
              Upload Excel, PDF, or receipt image - AI extracts expenses automatically
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              id="file-upload"
              accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:bg-accent/20 transition-colors">
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-12 w-12 text-primary animate-spin mb-3" />
                    <p className="text-sm text-muted-foreground">Processing file...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium text-foreground mb-2">Click to upload file</p>
                    <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <FileSpreadsheet className="h-4 w-4" />
                        <span>Excel/CSV</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FileText className="h-4 w-4" />
                        <span>PDF</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Image className="h-4 w-4" />
                        <span>Image</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Max 10MB</p>
                  </>
                )}
              </div>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mr-2" />
              Expenses Extracted
            </DialogTitle>
          </DialogHeader>
          
          {parsedData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">From: {parsedData.filename}</p>
                  <p className="font-bold text-lg text-emerald-900">{parsedData.expenses_found} expenses found</p>
                </div>
              </div>

              <ScrollArea className="h-96 pr-4">
                <div className="space-y-3">
                  {parsedData.expenses.map((expense, idx) => (
                    <Card key={idx} className="bg-accent/20">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Amount:</span>
                            <p className="font-semibold text-lg">
                              {expense.currency === 'INR' ? '₹' : '$'}{expense.amount}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Category:</span>
                            <p className="font-semibold">{expense.category}</p>
                          </div>
                          {expense.vendor && (
                            <div>
                              <span className="text-muted-foreground">Vendor:</span>
                              <p className="font-semibold">{expense.vendor}</p>
                            </div>
                          )}
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Description:</span>
                            <p className="font-semibold">{expense.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleUseInForm}>
              Edit in Form
            </Button>
            <Button onClick={handleSubmitExpenses} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Submitting...' : `Submit ${parsedData?.expenses?.length || 0} Expense(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FileUploadCard;
